import React from 'react';
import styled from 'styled-components';
import { Flex, Image, Text } from 'rebass';
import Frame from 'components/Frame';
import editIcon from 'assets/icons/edit.png';
import verifiedIcon from 'assets/icons/verified.png';
import mailIcon from 'assets/icons/mail.png';
import phoneIcon from 'assets/icons/phone.png';
import locationIcon from 'assets/icons/location.png';
import colors from 'theme/colors';
import { useSelector } from 'react-redux';
import Link from 'components/Link';
import { RECRUITER, EMPLOYER } from 'constants/index';
import { UserState } from 'types/global';

const SectionWrapper = styled(Flex)`
  border-bottom: 4px solid ${colors.gray};
`;

const BioText = styled(Text)`
  border: 2px solid ${colors.darkGray};
  min-height: 480px;
`;

interface State {
  user: UserState;
}

const Profile: React.FC = (): JSX.Element => {
  const user = useSelector<State, UserState>(state => state.user);

  const {
    status,
    email,
    addressInfo: { address, country, city, province },
    info: { contactPerson, contactPersonTitle, companyName, phoneNumber },
  } = user;

  return (
    <Flex py={9}>
      <Frame>
        <Flex width="100%" flexDirection="column">
          <SectionWrapper width="100%" justifyContent="space-between" alignItems="center" pb={3}>
            <Flex flexDirection="column">
              <Flex alignItems="center" mb={2}>
                <Text as="h2" fontSize="36px" fontWeight={6} mr={2}>
                  {contactPerson}
                </Text>
                {status === 'ACTIVE' && <Image src={verifiedIcon} />}
              </Flex>
              <Text as="p" fontSize="24px" fontWeight={6}>
                {`${contactPersonTitle} at ${companyName}`}
              </Text>
            </Flex>
            <Flex flexDirection="column">
              {user.role === RECRUITER && (
                <Text fontSize="24px" fontWeight={6} mb={2}>
                  License Number: {user.info.recruitingLicence}
                </Text>
              )}
              <Link to="/profile/edit" mt={user.role === EMPLOYER ? 6 : 0}>
                <Flex width="100%" justifyContent="flex-end" alignItems="center">
                  <Image src={editIcon} mr={1} />
                  <Text fontSize="24px" fontWeight={6} color="lightGreen">
                    Edit Info
                  </Text>
                </Flex>
              </Link>
            </Flex>
          </SectionWrapper>
          <SectionWrapper justifyContent="center" py={4} mb={9}>
            <Flex width="100%" justifyContent="space-between" css="max-width: 1065px;">
              <Flex alignItems="center">
                <Image src={mailIcon} mr={3} />
                <Text as="p" fontSize="24px" fontWeight={6}>
                  {email}
                </Text>
              </Flex>
              <Flex alignItems="center">
                <Image src={phoneIcon} mr={3} />
                <Text as="p" fontSize="24px" fontWeight={6}>
                  {phoneNumber}
                </Text>
              </Flex>
              <Flex alignItems="center">
                <Image src={locationIcon} mr={3} />
                <Flex flexDirection="column">
                  <Text as="p" fontSize="24px" fontWeight={6} mb={1}>
                    {country}, {city}, {province}
                  </Text>
                  <Text as="p" fontSize="24px" fontWeight={6}>
                    {address}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </SectionWrapper>
          <Flex flexDirection="column">
            <Text as="h2" fontSize="28px" fontWeight={6} mb={5}>
              {user.role === RECRUITER ? 'Recruiter Bio' : 'Company Bio'}
            </Text>
            <BioText fontSize="24px" p={4}>
              {user.role === EMPLOYER ? user.info.companyBio : ''}
            </BioText>
          </Flex>
        </Flex>
      </Frame>
    </Flex>
  );
};

export default Profile;
